import {
  ObjectEventData,
  ObjectType,
  RoomNodeStyle,
  Alignment_Horizontal,
  BreakoutGroupData,
  CanvasFocus,
  RoomNavigationType,
  type Side,
  type ClientAnswer,
  type CANVAS_SPEED,
  type InfoGraphicHotspotData,
  type ImageSizes,
  type BreakoutClientData,
  ShapeType,
  ShapePresetStyle,
  LineEndpointType,
} from './shared/api_shared_types';

import type Hls from 'hls.js';
import * as HLS from 'hls.js';
import type { RenderPackageItem } from './render_package';
import { TextObject2EditMode } from './textobject/textobject2_types';
import type { Line } from './textobject/textobject2_types';

export type FetchResponse = {
  message: string;
  status: string;
  data: any;
};

export type GameObjectController = {
  getNew: (ent: Entity) => GameObject;
  sync(manager: GameManager, obj: GameObject, data: ObjectEventData): void;
  update: (...args: any[]) => boolean;
  postUpdate?: (...args: any[]) => boolean;
  createPackage?(
    object: GameObject,
    position: V2,
    scale: number,
    opacity: number,
  ): RenderPackageItem[];
  dispose?(object: GameObject): void;
};

export type GameObjectControllerMap = { [key in ObjectType]: GameObjectController };

export type GameObjectRegistry = {
  byType: Map<ObjectType, Set<GameObject>>;
};

export type ObjectEventDataMap = { [key in number]: ObjectEventData[] };

export type FetchCallback = (response: FetchResponse) => void;

export type V2 = {
  x: number;
  y: number;
};

export type Size = {
  width: number;
  height: number;
};

export type TransformSize = {
  sizePx: Size;
  sizeEm: Size;
};

export type TransformFullSize = {
  width: number;
  height: number;
  relativeWidth: number;
  relativeHeight: number;
  padWidth: number;
  padHeight: number;
  maxWidth: number;
  maxHeight: number;
  maxAspect: number;
};

export type TransformRadius = {
  radiusPx: number;
  radiusEm: number;
};

export type GameObjectIdTable = { [key: number]: GameObject };

export type DeltaTime = number;
export type Time = number;

export enum VisibilityState {
  NONE = 'none',
  HIDDEN = 'hidden',
  HIDING = 'hiding',
  DISAPPEARING = 'disappearing',
  DEFAULT = 'default',
  APPEARING = 'appearing',
}

export enum Shape {
  NONE,
  RECT,
  CIRCLE,
  BLOB,
}

export type ClientObjectId = number;
export type ServerObjectId = number;
export type RoomId = string;

export type EntityUpdate = {
  width: number;
  height: number;
  posX: number;
  poxY: number;
  view: boolean;
  childrenTransform: boolean; //did any of my children update their size or position?
  childrenView: boolean; //did any of my children update their view?
};

export type User = {
  sessionId: string;
  accessToken: string;
  refreshToken: string;
  firstName: string;
  lastName: string;
  profilePicSizes: ImageSizes;
  slug: string;
  email: string;
  cmsRole: boolean;
};

export type Transform = {
  debug: string;
  layoutChanged: boolean;
  influenceKinetic: boolean;
  pushBack: boolean;
  pushBackChildren: boolean;
  force: V2;
  vel: V2;
  pos: V2;

  rotation: number;

  scale: number;
  pivotPoint: V2;
  childAnchorPivot: V2; //Anchored children use this relative point as anchor
  anchoredPos: AnchoredPosition | null;
  shape: Shape;
  radius: number;
  width: number;
  height: number;
  relativeWidth: number; //when above 0, relative with based on parent width (0-1)
  relativeHeight: number; //when above 0, relative height based on parent height (0-1)
  padWidth: number; //absolute number added or subtracted after relativeWidth is applied
  padHeight: number; //absolute number added or subtracted after relativeHeight is applied
  maxWidth: number;
  maxHeight: number;
  maxAspect: number;
  disableAutoSize: boolean;
};

export type Entity = Transform & {
  doKill: boolean;

  manager: GameManager | null;
  id: ClientObjectId;
  serverObjId: ServerObjectId; //-1 when not server-controlled

  parent: GameObject | null;
  children: GameObject[];
  isAttached: boolean;
  attachChild(child: GameObject, parent: GameObject): void;
  detachChild(child: GameObject, parent: GameObject): void;

  parentRoomId: RoomId;
  lifeTime: Time;
  localZIndex: number;
  fixedZIndex: boolean; //when true, this object wont move to a different zIndex when a child does
  alwaysOnTop: boolean; //when true, this object is always rendered above siblings that do not have alwaysOnTop
  autoDisappearTime: Time;
  moveMode: MoveMode;
  isDirty: boolean; //true when changed this frame
  //
  visibilityState: VisibilityState;
  entityStateChanged: boolean;
  stateChangedTime: number;
  stateProgress: number;
  appearDuration: number;
  disappearDuration: number;
  appearAnimation: (e: GameObject, p: number) => void;
  disappearAnimation: (e: Entity, p: number) => void;
  dispose(object: GameObject): void;

  animations: PlaygroundAnimationCollection[];

  requestTextInput: boolean; //When true, the cursor shows the text-input

  //interaction

  dragged: boolean;
  dragPos: V2;

  relativeMousePos: V2;

  syncType: EntitySyncType;

  attachpoints: AttachPoint[];

  showTypeHintOnHover: boolean;
  highlighted: boolean;
  highlightLastFrame: boolean;
  startHighlightThisFrame: boolean;
  stopHighlightThisFrame: boolean;
  clickedThisFrame: boolean;
  droppedThisFrame: boolean;
  clickable: boolean;
  allowHighlight: boolean;
  allowChildHighlight: boolean;
  allowDrag: boolean;
  passHover: boolean;
  passClick: boolean;
  passClickCanvas: boolean;
  passDrag: boolean;
  mouseOver: boolean;
  mouseDownOnMe: boolean;
  mouseDownTime: number;
  topHighlightedChild: Entity;

  //style
  renderShadow: RenderShadow | null;
  renderShadowDrag: RenderShadow | null;
  opacity: number;
  doClipChildren: boolean;
};

export type ShapeStyleEntity = Entity & {
  shapeStyle: RenderShapeStyle;
};

export enum EntitySyncType {
  NONE,
  SIMPLE,
  REAL_TIME,
}

export type PoolController<t> = {
  freeObjects: t[];
  usedObjects: t[];
  poolSize: number;
};

export type AnchoredPosition = {
  referenceId: number; //reference to entity id, when 0, parent is used as ref
  relativePos: V2;
  offset: V2;
  relativePivotPos: V2; //0 to 1, defines which point in the object needs to align with the anchored position
};

export type AttachPoint = {
  tag: string;
  pos: V2;
  dir: V2;
};

export type Connection = Entity & {
  objectType: ObjectType.CONNECTION;
  entities: ConnectionEntity[];
  maxDistance: number;
  minDistance: number;
  style: LineStyle;
  hidden: boolean;
  alpha: number;
  lineEndPointType1: LineEndpointType;
  lineEndPointType2: LineEndpointType;
};

export type ConnectionEntity = {
  serverObjId: ServerObjectId;
  ent: Entity;
  attachpoints: string[];
};

export type LineStyle = {
  color: Color;
  dash: number[];
  width: number;
  bezier: boolean;
};

export type InsertionPoint = {
  pos: V2;
  blickTimeStart: number;
};

export type GameObject =
  | Space
  | ParticleSystem
  | Room
  | BaseObject
  | Connection
  | Container
  | Cursor
  | Canvas
  | DOMObject
  | TextObject
  | TextObjectSimple
  | TextObject2
  | UserText
  | UserTextEditor
  | TextEditor
  | IconButton
  | TextButton
  | Slider
  | CmsConsole
  | VideoPlayer
  | QRCode
  | DropArea
  | RoomNode
  | ImageObject
  | ImageEnt
  | InfoGraphic
  | RoomUserEntity
  | ProgressMeter
  | BreakoutController
  | ShapeObject
  | TextInput
  | ToolTip;

export type ObjectEvent = {
  eventData: ObjectEventData;
  gameObject: GameObject;
};

export type BaseObject = Entity & {
  objectType: ObjectType.BASE;
};

export type ToolTip = Entity & {
  objectType: ObjectType.TOOLTIP;
  tObj: TextObject;
  btnClose: IconButton;
  flag: string;
  text: string[];
  currentPage: number;
  btnNext: TextButton;
  btnPrev: TextButton;
  onFlagSet: ((doContinue: boolean) => void) | null;

  arrowSide: Side;
  arrowOffset: number;
  arrowWidth: number;
  arrowLength: number;
  cornerRadius: number;
  shapeStyle: RenderShapeStyle;
  showNextButton: boolean;
  shownAt: number;
};

export type Container = Entity & {
  objectType: ObjectType.CONTAINER;
  doClear: boolean;
  shapeStyle: RenderShapeStyle;
  highlightStyle: RenderShapeStyle;
  defaultStyle: RenderShapeStyle;
  cornerRadius: number;
  cornerRadius1: number;
  cornerRadius2: number;
  cornerRadius3: number;
  cornerRadius4: number;
  highlightSpeed: number;
  defaultSpeed: number;
};

export type DOMObject = Entity & {
  objectType: ObjectType.DOM_ELEMENT;
  wrapperEl: HTMLDivElement;
  elements: HTMLElement[];
  defaultUpdate: boolean;
  centerContent: boolean;
  cornerRadius: number;
  contentWidth: number;
  contentHeight: number;
  contentMatchHeight: boolean;
  contentMatchWidth: boolean;
  contentAspect: number;
  clearContent: boolean;
  domZIndex: number;
  shapeStyle: RenderShapeStyle;
  currentWrapperElStyle: {
    x: number;
    y: number;
    width: number;
    height: number;
    scale: number;
    zIndex: number;
  };
  currentContentElStyle: {
    top: number;
    left: number;
    width: number;
    height: number;
    zIndex: number;
  };
};

export type TextObject = Entity & {
  objectType: ObjectType.TEXTOBJECT;

  // references
  words: Word[];
  textEnt: TextEnt;
  hoverContainers: { container: Container; word: Word; actionId: string }[];
  closeBtn: IconButton;

  // Only used for cursor text now
  // insertionPoint: InsertionPoint | null;

  // Data
  text: string;
  maxWidthPx: number; //given by user, to force line breaks
  fontSizePx: number;

  maxLines: number;

  // style
  textStyle: TextObjectStyle;
  removeDoubleWhiteSpaces: boolean;
  removeEmptyLinesOnEnd: boolean;
  shapeStyle: RenderShapeStyle | null;

  currentTextFade: number;

  // should be style?
  maxWidthEm: number; //given by user, to force line breaks
  minWidthMod: number; //a text can never be smaller than the longest word.
  textShadow: RenderShadow;

  //other properties
  allowEdit: boolean;
  textChanged: boolean;

  hoverWords: boolean;
  changeDuration: number;
  question: boolean;
  hideAllText: boolean;
  onlyShowInDefaultState: boolean;

  appearStyle: TextAppearStyle;
  backgroundAlpha: number;

  autoTypeController: AutoTypeController;

  linkPopup: TextObject;
  linkPopupDisappearTime: number;
};

export type TextObject2 = Entity & {
  objectType: ObjectType.TEXTOBJECT2;
  textOverflow: '' | 'ellipsis' | 'wrap';
  editMode: TextObject2EditMode;

  defaultTextStyle: TextStyle;

  // Data
  lines: Line[];

  text: string;
  maxLength: number;
  canEditSize: boolean;
  handle: Container | null;
  padding: [number, number, number, number];
  editTextArea: HTMLTextAreaElement | null;
  _wrapWidth: number;
  _editAreaDirty: boolean;
  _isEditing: boolean;
  _pendingRemoval: boolean;
  _pendingFocus: boolean;
  _pendingCaretOffset: number;
  _dragSelectionAnchor: number | null;
  _hasTextCursor: boolean;
};

export type TextObjectSimple = Entity & {
  objectType: ObjectType.TEXTOBJECT_SIMPLE;
  textOverflow: '' | 'ellipsis';

  defaultTextStyle: TextStyle;

  // Data
  text: string;
  visibleText: string;
  _ellipsisWidth: number;
};

export enum TextAppearStyle {
  NONE,
  AUTO_TYPE,
  FADE,
  FADE_AND_MOVE_RIGHT,
  FADE_AND_MOVE_LEFT,
  FADE_AND_MOVE_UP,
  FADE_AND_MOVE_DOWN,
}

export type TextEnt = {
  pos: V2;
  posOffset: V2;
  width: number;
  height: number;
  textBlocks: TextBlock[];
  hoverBlock: TextBlock;
  hoverLine: TextLine;
  hoverWord: Word;
};

export type TextBlock = {
  pos: V2;
  width: number;
  height: number;
  words: Word[];
  lines: TextLine[];
  type: TextBlockType;
  maxWidthPx: number;
  hover: boolean;
};

export enum TextBlockType {
  NONE,
  WHITE_SPACE,
  ALINEA,
  LIST_ITEM,
}

export type TextLine = {
  localPos: V2;
  pos: V2;
  width: number;
  height: number;
  words: Word[];
  lineNr: number;
  hover: boolean;
};

export type Word = {
  type: WordType;
  text: string;
  visibleText: string;
  localPos: V2;
  pos: V2;
  offset: V2;
  width: number;
  height: number;
  textColor: Color | null; //when null, default text color of textobject is used
  colorAlpha: number; //0 - 255, where 0 is transparant and
  wordStyle: WordStyle;
  clickActionId: string;
  command: Command;
  hover: boolean;
};

export type UserText = Entity & {
  objectType: ObjectType.USER_TEXT;

  text: string;
  textObject: TextObject | null;

  editor: UserTextEditor | null;
  startEditingThisFrame: boolean;
  stopEditingThisFrame: boolean;

  msg: string;
  msgText: TextObject | null;

  isLocked: boolean;

  requestStartEditMode: boolean;
  requestRemoveByClient: boolean;
  requestSyncToServer: boolean;

  isDropAreaInputMode: boolean;
};

export type UserTextEditor = Entity & {
  objectType: ObjectType.USER_TEXT_EDITOR;
  userText: UserText;
  tObj: TextObject;
  removeBtn: IconButton;
  inputElement: HTMLTextAreaElement;
  previousMoveMode: MoveMode;
  hasFocus: boolean;
  shapeStyle: RenderShapeStyle;
};

export type TextEditor = Entity & {
  objectType: ObjectType.TEXT_EDITOR;
  tObj: TextObject;
  showBorder: boolean;
  inputElement: HTMLTextAreaElement;
  stopEditingThisFrame: boolean;
  previousMoveMode: MoveMode;
  autoFinishEditor: boolean;
  hasFocus: boolean;
};

export type TOBJ = Entity & TextObject;

let ent: TOBJ;
foo(ent);
function foo(bar: Entity) {}

export enum PADDING {
  TOP = 0,
  RIGHT = 1,
  BOTTOM = 2,
  LEFT = 3,
}

export enum SIDE {
  LEFT = 0,
  RIGHT = 1,
}

export type VideoPlayer = Entity & {
  objectType: ObjectType.VIDEOPLAYER;
  videoUrl: string;
  captionsUri: string;
  domObj: DOMObject;
  elementID: string;
  element: HTMLElement;
  subsElement: HTMLElement;
  videoElement: HTMLVideoElement;
  parsedVTTCues: { start: number; end: number; text: string }[] | null;
  onVTTTimeUpdate: (() => void) | null;
  playState: PlayState;
  hls: Hls;
  bottomContainer: Container;
  videoElementWidth: number;
  videoElementHeight: number; //actual size of the video iFramce laying underneath the canvas
  controlsVisible: boolean;
  videoAspect: number; //the aspect ratio of the video source;

  autoPlay: boolean;
  showSyncPlayTip: boolean;
  requestPlay: boolean;
  requestPause: boolean;
  requestPlayTime: number;
  currentPlayTime: number;
  duration: number;
  loop: boolean;
  playerReady: boolean;
  readyToPlayLocal: boolean;
  readyToPlaySync: boolean;

  allowUserPlay: boolean;
  hideControls: boolean;
  buttonPlayPause: IconButton;
  buttonSubs: IconButton;
  btnMinimize: IconButton;
  playSlider: Slider;
  previousPlayTime: number; //the play position of the previous frame
  localPlayInterupted: boolean; //set true when video is playing but not making progress (probably buffering)
  localPlayInteruptedSynced: boolean; //set true when video is playing but not making progress (probably buffering)
  syncedPlayInterupted: boolean; //set true when another user's video is playing but not making progress (probably buffering)
  syncedPlay: boolean;
  msg: string;
  msgDuration: number;
  msgTime: number;
  defaultWidthPx: number;
  msgTextObject: TextObject | null;
  style: '' | 'default' | 'polaroid' | 'minimized';
  title: string;
  tObjTitle: TextObject;
  tObjTime: TextObject;
  imageUri: string;
  container: Container;
  thumbnail: ImageEnt;
  playIcon: ImageEnt;
  playerSizeState: VideoPlayerSizeState;
  sizeAnimations: SizeAnimation[];
  handleHLSError: (e: HLS.Events.ERROR, data: HLS.ErrorData) => void;
  retryLoadSourceTime: number;
  lastSeekTime: number;
  showSubs: boolean;
  showSubsTimeout: ReturnType<typeof setTimeout> | null;
};

export enum VideoPlayerState {
  NONE,
  NOT_READY,
  REMOTE_NOT_READY,
  PAUSED,
  PLAYING,
  INTERUPTED_LOCAL,
  INTERUPTED_REMOVE,
}

export enum VideoPlayerSizeState {
  NONE = '',
  TO_MINIMIZED = 'TO_MINIMIZED',
  TO_MINIMIZED_FORCED = 'TO_MINIMIZED_FORCED',
  MINIMIZED = 'MINIMIZED',
  TO_DEFAULT = 'TO_DEFAULT',
  DEFAULT = 'DEFAULT',
  DEFAULT_LOCKED = 'DEFAULT_LOCKED', //when playing/paused, the size stays default on hightlight-end
}

export type QRCode = Entity & {
  objectType: ObjectType.QRCODE;
  text: string;
  image: any;
  ready: boolean;
};

export type ImageObject = Entity & {
  objectType: ObjectType.IMAGE_OBJECT;
  label: string;
  imageUri: string;

  maxImageWidthEm: number;
  minImageWidthEm: number; //for very small, height images, this width is used as minimum, so there could be white bars left and right
  maxImageHeightEm: number;
  fixedSize: boolean;

  imageEnt: ImageEnt;
  previousImageEnt: ImageEnt;
  textObject: TextObject;

  requestImageUpload: boolean;
  isWaitingForResponse: boolean;
  uploadedImageId: string;
  uploadedImageUrl: string;

  backgroundColor: Color;
};

export type InfoGraphic = Entity & {
  objectType: ObjectType.INFOGRAPHIC;

  widthEm: number;
  textWidthEm: number;
  defaultText: string;

  imageUri: string;
  imageEnt: ImageEnt;

  horTextOffsetEm: number;
  vertTextOffsetEm: number;

  textAlign: Alignment_Horizontal;
  textPlacement: Side;
  defaultTextStyle: string;

  activeHotspotIndex: number;

  tObj: TextObject;
  // boxedText: boolean;
  showBorder: boolean;

  hotspots: InfoGraphicHotspot[];
};

export type InfoGraphicHotspot = InfoGraphicHotspotData & {
  container: Container;
  tObj: TextObject;
};

export type ImageUploadResponse = {
  _id: string;
  uri: string;
};

export enum PlayState {
  NONE = '',
  STOPPED = 'stopped',
  PAUSED = 'paused',
  PLAYING = 'playing',
}

export type Canvas = Entity & {
  objectType: ObjectType.CANVAS;
  centerAnchor: Container;
  debugView: boolean;
  layer: Container;
  centerContainer: Container;
  debugFocusContainer: Container;
  scheduleUpdateLayout: boolean;
  layerDragOffset: V2;
  focus: CanvasFocus;
  stickToDefaultFocus: boolean;
  focusPosition: V2;
  requestApplyFocus: { focus: CanvasFocus; speed: CANVAS_SPEED };
  focusSize: Size;
  relativeScreenFocusPoint: V2;
  normalizedZoomLevelTarget: number;
  allowScrollVert: boolean;
  allowScrollHor: boolean;
  allowZoom: boolean;
  noBorders: boolean;
  listMode: boolean;

  doCheckFocus: boolean;
  isOutOfFocus: boolean;
  focusCheckCooldown: number;
  canvasMovedThisFrame: boolean;
  refocus: CanvasRefocus;
  showNavigationTip: boolean;
  showRefocusTip: boolean;

  autoSize: boolean;
  scrollAnimation: PositionAnimation;
  zoomAnimation: ScaleAnimation;

  layerBasePos: V2;
  minLayerWidth: number;
  minLayerHeight: number;
  leftBound: number;
  rightBound: number;
  topBound: number;
  bottomBound: number;
  zoomSlider: Slider;
  showScrollbar: boolean;

  linked: Container;
  // linkedCanvas: Canvas;

  //this is a value from 0 to 100 to create a linear zoom feeling,
  // note that it is not equal to the scale percentage
  normalizedZoomLevel: number;
  scrollSliderHor: Slider;
  onScaleChange: (canvas: Canvas, scale: number) => void;
};

export type CanvasRefocus = {
  container: Container;
  btn: IconButton;
};

export type Corners = [V2, V2, V2, V2];

export type Color = [number, number, number] | [number, number, number, number];

export enum MoveMode {
  NONE = 'none',
  STATIC = 'static',
  KINETIC = 'kinetic',
  ANCHORED = 'anchored',
  CURSOR = 'cursor',
}

export type Cursor = Entity & {
  objectType: ObjectType.CURSOR;
  enabled: boolean; //false when not focused on canvas
  rot: number;
  targetRot: number;
  size: number;
  prevVel: V2[];
  prevPos: V2;
  prevPosDt: number[];
  text: string;
  placeholder: string;
  changed: boolean;
  userText: UserText;
  tObjPlaceholder: TextObject;
  // tObjTooltip: TextObject;
  // tooltip: string;
  // requestChangeTooltip: string;
  // requestHideTooltip: string;

  focussedThisMouseDown: boolean;

  highlightEntities: Entity[];
  highlightTopEntity: Entity;
  highlightCanvasses: Canvas[];

  maybeDragEntity: Entity;
  draggedEntity: Entity;
  startDraggingEntityThisFrame: Entity; //the entity that the cursor started to drag in this frame
  canceledDraggingEntityThisFrame: boolean; //the entity is not dragged anymore, but also not actively dropped by the user (for instance when it disappears)
  droppedEntityThisFrame: Entity; //the entity that the cursor dropped in this frame
  mouseDownOnEntity: Entity;
  mouseDownTime: number;
  idleTime: number;
  timeSinceLastTextTyped: number; //time since the last cursor text has changed
  timeSinceLastTextInput: number; //time since the last text object has been put on the playgroud
  closestTextObject: TextObject;
  closestPointOnTextObject: V2;
  style: CursorStyle;
  allowTyping: boolean;
  allowCreatingText: boolean;
  inputRequestTextObject: TextObject;
  inputRequestEntityCount: number;
  inputRequestLineTransparancy: number;
  clickToContinue: boolean;

  textEditor: TextEditor;
};

export enum CursorStyle {
  simple_white,
  colored,
}

export type UserRoomId = string;

export type Room = Entity & {
  objectType: ObjectType.ROOM;
  roomId: UserRoomId;
  name: string;

  subTitle: string;
  message: string;
  closeRoomLabel: string;
  joined: boolean;
  useNewVideocallUI: boolean;
  users: RoomUser[];
  syncCursorText: boolean;
  syncUserText: boolean;
  syncCursor: boolean;
  showCursor: boolean;
  navigationType: RoomNavigationType;

  content: Container;
  instructionLayer: Container;
  lobbyLayer: Container;
  topUILayer: Container;

  canvas: Canvas | null;
  showCanvas: boolean;
  userIsHost: boolean;
};

export type RoomUser = {
  firstName: string;
  lastName: string;
  slug: string;
  profileUriThumb: string;
  profileUriMedium: string;

  roomUserIndex: number;
  userSlug: string; // to replace clientId
  isReserved: boolean;

  isHost: boolean;

  disconnected: boolean;

  roomUserEntity: RoomUserEntity | null;
};

export type PerformanceTier = 0 | 1 | 2;

export type GameManager = {
  serverSession: string;
  isMobile: boolean;
  localTimeZone: string;
  pageTitle: string;
  preventScroll: boolean;
  clientReady: boolean;
  gameObjectIdTable: GameObjectIdTable;
  gameObjectServerIdTable: GameObjectIdTable;
  objectControllerMap: Partial<GameObjectControllerMap>;
  doUpdatePhysics: boolean;
  space: Space;
  spaceBG: Space;
  entities: Entity[];
  cursor: Cursor;
  user: User;
  mainRoom: Room;

  autoTypeControllers: AutoTypeController[];
  DOM: PlaygroundDOM;
  textRenderCtx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

  objectEventData: ObjectEventDataMap;
  paused: boolean;
  mousePos: V2;
  lastMousePos: V2;
  isConnectedToServer: boolean;
  lastEntityId: number;
  userEmit: UserEmit;

  backgroundMusic: BackgroundMusic;
  requestCloseOverlayEntities: boolean;
  rootFontSizePx: number;
  rootUnitSizePx: number;
  animations: PlaygroundAnimationCollection[];
  settings: Settings;

  registry: GameObjectRegistry;

  performanceTier: PerformanceTier;
  performanceTierChanged: boolean;
};

export type Settings = {
  offScreenCanvasEnabled: boolean;
  helpUri: string;
  permissionsHelpUri: string;
  videoCallInfoTitle: string;
  videoCallInfoContent: string;
};

export type PermissionsController = {
  permissionCheckAllowed: boolean;
  permissionCheckCompleted: boolean;
  waitingForPermissionState: boolean;
  cameraPermission: PermissionStatus;
  microphonePermission: PermissionStatus;
  cameraPermissionState: PermissionState | null;
  microphonePermissionState: PermissionState | null;
};

export type DeviceSelection = {
  cameraLabel: string;
  micLabel: string;
  speakerLabel: string;

  cameraId: string;
  micId: string;
  speakerId: string;
};

export type PlaygroundDOM = {
  ctxBackground: CanvasRenderingContext2D;
  ctxMain: CanvasRenderingContext2D;
  backCanvasElement: HTMLCanvasElement;
  canvasElement: HTMLCanvasElement;
  backgroundContainer: HTMLElement;
  backgroundVideoContainer: HTMLElement;
  blurContainer: HTMLElement;
  videoContainer: HTMLElement;
  audioContainer: HTMLElement;
  overlayContainer: HTMLElement;
  blurLayer: HTMLElement;
  connectionNotification: HTMLElement;
  connectionNotificationMessage: HTMLElement;
  cursorCircle: SVGElement;
};

export type BackgroundLayer = {
  el: HTMLElement;
  lifeTime: number;
  doKill: boolean;
};

export type BackgroundMusic = {
  currentUrl: string;
  volume: number;
  loop: boolean;
  requestPlayTrack: string;
  requestStopTrack: boolean;
};

export type Module = {
  id: string;
  name: string;
  icon: string;
};

export type ImageId = string;
export type ImageTable = { [index: ImageId]: PlaygroundImage };

export type PlaygroundImage = {
  state: ImageState;
  img: HTMLImageElement;
  uri: string;
  width: number;
  height: number;
  aspect: number;
  onLoad: (HTMLELement: HTMLElement) => void;
};

export type ImageState = '' | 'loading' | 'error' | 'success';

export type PlaygroundFont = {
  id: string;
  fontFace: FontFace;
  baseLineOffsetPerSize: number;
  loaded: boolean;
};

export type Icon = {
  entity: Entity;
  image: any;
};

export enum SliderOrientation {
  NONE,
  HORIZONTAL,
  VERTICAL,
}

export type Slider = Entity & {
  objectType: ObjectType.SLIDER;
  orientation: SliderOrientation;
  min: number;
  max: number;
  valuePercentage: number;

  secondValuePercentage: number;
  barColorSecond: Color;

  useThirdBar: boolean;
  thirdValuePercentage: number;
  barColorThird: Color;

  valueInt: number;
  maxBarWidth: number;
  barWidth: number;
  barColorBack: Color;
  barColorValue: Color;
  startUnSet: boolean;
  valueSet: boolean;
  knobPos: V2;
  knobSpeed: number;
  knob: Container;
  answerObject: SliderAltAnswerObject;
  altAnswerObjects: SliderAltAnswerObject[];
  altAnswers: ClientAnswer[];
  knobChanged: boolean;
  knobIsDragged: boolean;
  knobFontSize: number;
  updateValue: boolean;
  valueSetOnce: boolean;
  showContinueButton: boolean;
  continueButton: TextButton;
  locked: boolean;
  requestSetNewValue: boolean;
  newValue: number;
  valueSetByUserThisFrame: boolean;
  applyButtonLabel: string;
  doShowInfo: boolean;
  tObjInfo: TextObject;
  infoText: string;
  infoIsShown: boolean;
  snapToInt: boolean;
  tooltip: string;
  tObjTooltip: TextObject;

  labels: string[];
  showCurrentLabel: boolean;
};

export type SliderAltAnswerObject = {
  container: Container;
  imageEnt: ImageEnt;
  tObj: TextObject;
  color: Color;
  pos: V2;
  tooltip: string;
};

export type DebugState = {
  isDebugMode: boolean;
  isDebugModeChangedThisFrame: boolean;
  showEntityCounts: boolean;
  showEntityInfo: boolean;
  showCmsConsole: boolean;
  alwaysRedraw: boolean;
  doDebugSkip: boolean;
  usePools: boolean;
  logDirty: boolean;
  pauseUpdate: boolean;
};

export type UserEmit = {
  mousePos: V2;
  mouseClicked: boolean;
  emitInterval: number;
  lastEmitTime: number;
  lastEmitX: number;
  lastEmitY: number;
  lastEmitText: string;
  cursorText: string;
};

export type RoomUserEntity = Entity & {
  objectType: ObjectType.OTHER_CLIENT;

  userSlug: string;
  targetPos: V2;
  lastClickedTime: Time;
  cursorText: string;
  cursorTextChanged: boolean;
  cursorTextObject: TextObject;
  showCursor: boolean;
  name: string;
};

export type TextButton = Entity & {
  objectType: ObjectType.TEXT_BUTTON;
  clickAndHoldDuration: number;
  clickAndHoldContainer: Container;
  clickAndHoldTime: number;
  executeThisFrame: boolean;
  container: Container;
  hoverContainer: Container;
  textObject: TextObject;
  command: Command;
  icon: ImageEnt;
  playSFX: boolean;
  showLoader: boolean;
  hintText: string;
  hintTextObject: TextObject;
  widthEm: number;
  animType: '' | 'move' | 'zoom';
  textPadding: number;
  padding: number;
  paddingNoLabel: number;
  onClick: (event: MouseEvent) => void;
};

export type IconButton = Entity & {
  objectType: ObjectType.ICON_BUTTON;

  targetIconScale: number;
  iconScale: number;

  offsetPos: V2;
  targetOffsetPos: V2;
  showOptions: boolean;
  highlightOption: number;
  colorBack: Color;
  color: Color;
  hoverLabel: string;
  hoverMoveStrength: number;
  hoverGrowStrength: number;
  style: ButtonStyle;
  requestNewStyle: ButtonStyle;
  checked: boolean;
  tooltip: string;
  label: string;
  labelSide: Side;
  iconRotation: number;
  iconRotationSpeed: number;
  tObjLabel: TextObject;
};

export type ButtonStyle = {
  icon: string;
  color: Color;
  colorHighlight: Color;
  size: number;
  iconSize: number;
  flipHorizontally?: boolean;
  hoverShadow: boolean;
};

export type TextStyle = {
  color: Color;
  fontSize: number;
  fontId: string;
  lineHeight: number;
};

export type TextObjectStyle = {
  color: Color;
  colorHighlight: Color;
  defaultTextColor: Color;
  fontSizeEm: number;
  doAutoResize: boolean;
  lineHeightMod: number;
  alineaSpacingMod: number;
  fontId: string;
  textAlign: Alignment_Horizontal;
  padding: number | number[];
  roundedSides: [boolean, boolean]; //Dont know alternative for now. When true [LEFT, RIGHT], that side is completely rounded
  cornerRadius: [number, number, number, number];
  strokeWeight: number;
  strokeColor: Color;
};

export type WordStyle = {
  color: Color;
  defaultTextColor: Color;
  fontSizeEm: number;
  fontSizePx: number;
  fontId: string;
};

export type LinkLine = {
  posA: V2;
  posB: V2;
};

export enum WordType {
  none = 'none',
  AlineaStart = 'AlineaStart',
  AlineaEnd = 'AlineaEnd',
  SentenceStart = 'SentenceStart',
  SentenceEnd = 'SentenceEnd',
  Word = 'Word',
  Space = 'Space',
  Period = 'Period',
  Comma = 'Comma',
  Slash = 'Slash',
  ExclamationMark = 'ExclamationMark',
  QuestionMark = 'QuestionMark',
  QuoteStart = 'QuoteStart',
  QuoteEnd = 'QuoteEnd',
  StyleTag = 'StyleTag',
  ListItemStart = 'ListItemStart',
  ListItemEnd = 'ListItemEnd',
  Ellipsis = 'Ellipsis',
  Anchor = 'Anchor',
}

export type AutoTypeController = {
  textObject: TextObject;
  currentCharCount: number;
  timeSinceLastCharTyped: number;
  currentTextIndex: number;
  textArray: string[];
  textTimeArray: number[];
  currentTextDone: boolean;
  timeBetweenChars: number;
  doKill: boolean;
};

export type Command = {
  type: CommandType;
  value: string;
  args: string[];
  // arg2?: string;
  // arg3?: string;
  pos?: V2;
  entity?: Entity;
};

export enum CommandType {
  NONE,
  ACTION,
  DEBUG,
}

export type ParticleSystem = Entity & {
  paused: boolean;
  objectType: ObjectType.PARTICLE_SYSTEM;
  particles: Particle[];
  influencers: ParticleInfluencer[];
  spriteSheet: SpriteSheet;
};

export type Particle = {
  pos: V2;
  vel: V2;
  acc: V2;
  dragCoefficient: number;
  shape: Shape;
  shapeStyle: RenderShapeStyle;
  rot: number;
  rotSpeed: number;
  size: number;
  animations: PlaygroundAnimation<any>[];
  image: any;
  spriteSheet: SpriteSheet;
  spriteIndex: number;
  spriteAnimation: boolean;
  animationTime: number;
  lifeTime: number;
  isDisappearing: boolean;
  maxLifeTime: number;
  wanderVel: V2;
  wanderSpeed: number;
  gravity: number;
  angle: number;
  debug: boolean;
  randomSeed: number;
  alpha: number;
  fadeAlpha: number;
  transparancyPerlin: PerlinController | null;
  sizePerlin: PerlinController | null;
  fadeOutDuration: number;
  doKill: boolean;
};

export enum AnimationType {
  NONE = '',
  PROGRESS = 'progress',
  SIZE = 'size',
  TRANSFORM_SIZE = 'tranform_size',
  SIZE_FULL = 'full_size',
  RADIUS = 'radius',
  POSITION = 'position',
  ROTATION = 'rotation',
  NUMBER = 'number',
  SCALE = 'scale',
  OPACITY = 'opacity',
  TRANSPARENCY = 'transparency',
  ANCHORED = 'anchored',
  SHAPE_STYLE = 'shape_style',
  IMAGE_SIZE = 'image_size',
}

export type PlaygroundAnimationCollection =
  | NumberAnimation
  | RotationAnimation
  | ScaleAnimation
  | TransformSizeAnimation
  | SizeAnimation
  | FullSizeAnimation
  | RadiusAnimation
  | AnchoredAnimation
  | StyleAnimation
  | ImageSizeAnimation
  | OpacityAnimation
  | PositionAnimation;

export type PlaygroundAnimation<T> = {
  doKill: boolean;
  type: AnimationType;
  delay: number;
  start: T;
  end: T;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: T;
  lerpFunction: (from: T, to: T, pEased: number) => T;
  prop: string;
};

export type PositionAnimation = {
  doKill: boolean;
  type: AnimationType.POSITION;
  delay: number;
  start: V2;
  end: V2;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: V2;
  lerpFunction: (from: V2, to: V2, pEased: number) => V2;
  prop: string;
};

export type RotationAnimation = {
  doKill: boolean;
  type: AnimationType.ROTATION;
  delay: number;
  start: number;
  end: number;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: number;
  lerpFunction: (from: number, to: number, pEased: number) => number;
  prop: string;
};

export type NumberAnimation = {
  doKill: boolean;
  type: AnimationType.NUMBER;
  delay: number;
  start: number;
  end: number;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: number;
  lerpFunction: (from: number, to: number, pEased: number) => number;
  prop: string;
};

export type TransformSizeAnimation = {
  doKill: boolean;
  type: AnimationType.TRANSFORM_SIZE;
  delay: number;
  start: TransformSize;
  end: TransformSize;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: TransformSize;
  lerpFunction: (from: TransformSize, to: TransformSize, pEased: number) => TransformSize;
  prop: string;
};

export type SizeAnimation = {
  doKill: boolean;
  type: AnimationType.SIZE;
  delay: number;
  start: Size;
  end: Size;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: Size;
  lerpFunction: (from: Size, to: Size, pEased: number) => Size;
  prop: string;
};

export type FullSizeAnimation = {
  doKill: boolean;
  type: AnimationType.SIZE_FULL;
  delay: number;
  start: TransformFullSize;
  end: TransformFullSize;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: TransformFullSize;
  lerpFunction: (
    from: TransformFullSize,
    to: TransformFullSize,
    pEased: number,
  ) => TransformFullSize;
  prop: string;
};

export type RadiusAnimation = {
  doKill: boolean;
  type: AnimationType.RADIUS;
  delay: number;
  start: TransformRadius;
  end: TransformRadius;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: TransformRadius;
  lerpFunction: (from: TransformRadius, to: TransformRadius, pEased: number) => TransformRadius;
  prop: string;
};

export type ScaleAnimation = {
  doKill: boolean;
  type: AnimationType.SCALE;
  delay: number;
  start: number;
  end: number;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: number;
  lerpFunction: (from: number, to: number, pEased: number) => number;
  prop: string;
};

export type OpacityAnimation = {
  doKill: boolean;
  type: AnimationType.OPACITY;
  delay: number;
  start: number;
  end: number;
  duration: number;
  easeFunction: (t: number) => number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  currentValue: number;
  lerpFunction: (from: number, to: number, pEased: number) => number;
  prop: string;
};

export type AnchoredAnimation = {
  doKill: boolean;
  type: AnimationType.ANCHORED;
  delay: number;
  start: AnchoredPosition;
  end: AnchoredPosition;
  duration: number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  easeFunction: (t: number) => number;
  currentValue: AnchoredPosition;
  lerpFunction: (from: AnchoredPosition, to: AnchoredPosition, pEased: number) => AnchoredPosition;
  prop: string;
};

export type StyleAnimation = {
  doKill: boolean;
  type: AnimationType.SHAPE_STYLE;
  delay: number;
  start: RenderShapeStyle;
  end: RenderShapeStyle;
  duration: number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  easeFunction: (t: number) => number;
  currentValue: RenderShapeStyle;
  lerpFunction: (from: RenderShapeStyle, to: RenderShapeStyle, pEased: number) => RenderShapeStyle;
  prop: string;
};

export type ImageSizeAnimation = {
  doKill: boolean;
  type: AnimationType.IMAGE_SIZE;
  delay: number;
  start: { minWidthEm: number; maxWidthEm: number; maxHeightEm: number };
  end: { minWidthEm: number; maxWidthEm: number; maxHeightEm: number };
  duration: number;
  t: number; //actual time in sec;
  p: number; //progress: normalized time 0 - 1;
  pEased: number; //progress: animation process 0 - 1 (but can be < 0 or > 1 based on ease function)
  easeFunction: (t: number) => number;
  currentValue: { minWidthEm: number; maxWidthEm: number; maxHeightEm: number };
  lerpFunction: (
    from: { minWidthEm: number; maxWidthEm: number; maxHeightEm: number },
    to: { minWidthEm: number; maxWidthEm: number; maxHeightEm: number },
    pEased: number,
  ) => { minWidthEm: number; maxWidthEm: number; maxHeightEm: number };
  prop: string;
};

export type ParticleInfluencer = {
  pos: V2;
  parentEntity: Entity;
  radius: number;
  outerStrength: number; //strength at outer radius: positive/negative -> attrackt/push
  innerStrength: number; //strength at center: positive/negative -> attrackt/push
  singleBurst: boolean;
};

export type PerlinController = {
  value: number;
  minValue: number;
  maxValue: number;
  valueRange: number;
  time: number;
  speed: number;
};

export type SpriteSheet = {
  imageId: ImageId;
  image: HTMLImageElement;
  rows: number;
  columns: number;
  spriteWidth: number;
  spriteHeight: number;
  positionTable: V2[];
  spriteCount: number;
  complete: boolean;
};

export type LayoutController = {
  lineNr: number;
  wordsOnLine: Word[];
  lineWidth: number;
  wordPacket: Word[];
  wordPacketWidth: number;
  currentY: number;
  longestLineWidth: number;
  lineHeight: number;
  textWidth: number;
  fontSizePx: number;
  indent: number;
};

export enum CORNER {
  TOP_LEFT = 0,
  TOP_RIGHT = 1,
  BOTTOM_RIGHT = 2,
  BOTTOM_LEFT = 3,
}

export enum RenderItemType {
  none,
  void,
  clip_start,
  clip_stop,
  group,
  point,
  line,
  bezier,
  rect,
  circle,
  arc,
  text,
  clearRect,
  clearShape,
  image,
  canvas,
  shape,
  tooltip_bubble,
}

export type RenderTransform = {
  position: V2;
  positionLocal: V2;
  rotation: number;
  scale: number;
};

export type RenderShapeStyle = {
  fillColor: Color;
  strokeWeight: number;
  strokeColor: Color;
  lineDash: number[];
  lineDashOffset: number;
  gradientId?: string;
  gradient?: boolean;
  gradientFade?: number;
  shadow: RenderShadow;
};

export type GradientData = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  colorStops: { offset: number; color: Color }[];
};

export enum TextAlignHor {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum TextAlignVert {
  TOP = 'top',
  CENTER = 'middle',
  BASELINE = 'alphabetic',
  BOTTOM = 'bottom',
}

export type RenderShadow = {
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
};

export type DropArea = Entity & {
  objectType: ObjectType.DROPAREA;

  acceptObjectTypes: ObjectType[];
  attachCandidate: GameObject; //If an object is being dragged, and could be attached
  willAttachOnDrop: boolean; //If this droparea will grab the candidate on drop
  attachedObject: GameObject;
  detachedObject: GameObject;
  showLabelOnAttach: boolean;
  labelAlignment: Alignment_Horizontal;

  previousParent: GameObject;
  previousMoveMode: MoveMode;

  defaultWidth: number;
  defaultHeight: number;

  enableDirectInput: boolean;

  grabber: Container;

  grabberTargetPos: V2;
  grabberTargetWidth: number;
  grabberTargetHeight: number;

  strokeColor: Color;
  style: DropAreaStyle;

  label: string;
  labelTObj: TextObject;
  characterLimit: number;
  showCharacterLimit: number;
  showCharacterLimitOnlyWhenEditing: boolean;
  characterCount: number;
  charCountTObj: TextObject;

  attaching: boolean;
  attachedThisFrame: boolean;
  detachedThisFrame: boolean;
  attachedTime: number;
  detachedTime: number;
  scheduledToUpdateServer: boolean;

  attachedTextStyle: TextObjectStyle;
  originalAttachedTextStyle: TextObjectStyle;
  attachedWidthEm: number;
  originalAttachedWidthEm: number;

  typeHintOnHover: boolean;
};

export enum DropAreaStyle {
  INACTIVE,
  ACTIVE,
  ACTIVE_HINT,
  ACTIVE_HINT_2,
  LOCKED,
}

export type Space = Entity & {
  objectType: ObjectType.SPACE;
  resizedThisFrame: boolean;

  requestResize: boolean;
  resizeApplied: boolean;
  lastWindowResizedTime: number;

  rooms: Container;
  cursor: Container;

  addRoom: (room: Room, window: Space) => void;
};

export type CmsConsole = Entity & {
  objectType: ObjectType.CMS_CONSOLE;

  sequenceSlug: string;
  sequenceTitle: string;
  stepName: string;
  entityInfo: string;
  sequenceState: string;
  debugMsg: string;
  nextStepNames: string[];

  reloadButton: TextButton;
  clearButton: TextButton;
  skipLobbyButton: TextButton;
  previousStepButton: TextButton;
  reloadStepButton: TextButton;

  text: TextObject;
  nextStepButtons: TextButton[];
};

export type ProgressMeter = Entity & {
  objectType: ObjectType.PROGRESS_METER;
  label: string;
  labelText: TextObject;
  minValue: number;
  maxValue: number;
  value: number;
  valuePercentage: number; //0-1
  styleBackground: RenderShapeStyle;
  styleBackgroundHL: RenderShapeStyle;
  styleFill: RenderShapeStyle;
};

export type RoomNode = Entity & {
  objectType: ObjectType.ROOM_NODE;

  highlightProcess: number;

  locked: boolean;
  label: string;
  infoMsg: string;
  iconId: string;
  style: RoomNodeStyle;

  tObjLabel: TextObject;
  tObjInfo: TextObject;

  imgLock: ImageEnt;
  img: ImageEnt;
  imgHover: ImageEnt;
};

export type BreakoutController = Entity & {
  objectType: ObjectType.BREAKOUT_CONTROLLER;

  slug: string;
  info: string;

  container: Container;
  groupCanvas: Canvas;

  stopTimer: number;

  tObjInfo: TextObject;

  tObjNotInGroup: TextObject;
  tObjNotReady: TextObject;

  stickToScreen: Boolean;

  totalMissionSteps: number;

  btnStartAllReady: TextButton;
  btnStopMenu: TextButton;
  btnStopAll: TextButton;
  btnStopAllTimed: TextButton;

  lobbyStateContainer: Container;
  btnState_preOpen: TextButton;
  btnState_Matching: TextButton;
  btnState_Closed: TextButton;
  btnState_Closed_Problem: TextButton;

  btnCreateGroups: TextButton;

  usersWithoutGroup: BreakoutClientData[];

  readyCollectionData: { count: number; users: number };
  startedCollectionData: { count: number; users: number };
  notStartedCollectionData: { count: number; users: number };
  emptyCollectionData: { count: number; users: number };
  finishedCollectionData: { count: number; users: number };

  groupData: BreakoutGroupData[];

  usersNotIngroupItem: UsersListItem;
  collectionListItems: CollectionListItem[];
  groupListItems: GroupListItem[];
};

export type CollectionListItem = {
  type: 'ready' | 'started' | 'empty' | 'finished' | 'not_started';
  data: BreakoutGroupData;
  container: Container;
  tObjTitle: TextObject;
  tObjGroupInfo: TextObject;
  tObj: TextObject;
  btnStart: TextButton;
  btnStop: TextButton;
};

export type UsersListItem = {
  container: Container;
  tObjTitle: TextObject;
  tObjUserGroup: TextObject;
  userViews: BreakoutUserView[];
};

export type GroupListItem = {
  id: number;
  data: BreakoutGroupData;
  container: Container;
  statusBar: Container;
  tObjTitle: TextObject;
  tObjStep: TextObject;
  tObjStatus: TextObject;
  tObjUserGroup: TextObject;
  userViews: BreakoutUserView[];

  btnStart: TextButton;
  btnContinue: TextButton;
  btnStop: TextButton;
  btnJoin: TextButton;
  btnLeave: TextButton;
  btnUngroup: TextButton;
};

export type BreakoutUserView = {
  id: number;
  container: Container;
  tObjName: TextObject;
  tObjStatus: TextObject;
  btnAdd: IconButton;
  btnRemove: IconButton;
};

export type ImageEnt = Entity & {
  objectType: ObjectType.IMAGE;

  maxWidthEm: number;
  minWidthEm: number;
  maxHeightEm: number;

  requestUpdateDimensions: boolean;

  appearStyle: 'none' | 'fade_and_scale' | 'fade';
  onImageError: 'white' | 'hide'; //what should be shown when the requested image cannot be shown

  requestNewImageId: string;
  imageId: string;
  aspect: number;
  imageState: ImageState;

  appearOnLoad: boolean;

  backgroundColor: Color;
  strokeColor: Color;
  strokeWeight: number;
  padding: number;

  rotationSpeed: number;

  alpha: number;
  appearAlpha: number;

  unloadImageOnDispose: boolean;
};

export type ImageDimensionSettings = {
  maxWidthEm: number;
  minWidthEm: number;
  maxHeightEm: number;
};

export type PreworkAssetClientData = {
  type: ObjectType;
  assetId: string;
  displayProperties: string[];
  name: string;
  clientId?: ClientObjectId;
  completed: boolean;
  required: boolean;
  props: {
    text: string[];
    videoId: string;
  };
};

export type ModuleExerciseClientData = {
  id: string;
  name: string;
  clientId?: ClientObjectId;
  displayProperties: string[];
  unlocked: boolean;
  completed: boolean;
  tooltip: string;
};

export type TextPageData = {
  text: string;
  audio: string;
  textObject: TextObject;
};

export { ObjectType as SharedObjectType };

export type AudioAnalyser = {
  id: string;
  source: MediaStreamAudioSourceNode | MediaElementAudioSourceNode;
  analyser: AnalyserNode;
  currentVolume: number;
};

export type ShapeObject = Entity & {
  objectType: ObjectType.SHAPE;
  shapeType: ShapeType;
  style: ShapePresetStyle;
  cornerRadius: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  styleFillColor: Color;
  styleLineWeight: number;
  styleLineColor: Color;
  lineStartType: LineEndpointType;
  lineEndType: LineEndpointType;
};

export type TextInput = Entity & {
  objectType: ObjectType.TEXT_INPUT;
  maxLength: number;

  shapeStyle: RenderShapeStyle;
  tObj: TextObject2;
  tObjTextLength: TextObjectSimple;

  value: string;
  locked: boolean;

  _isEditing: boolean;
};
